import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useActiveTenantId } from '@/store/tenantStore'
import { Badge, Card, PageHeader, Skeleton, useToast } from '@biartbt/biartd'
import { organizationsApi } from '@/modules/system/organizations/api/organizationsApi'
import { departmentsApi } from '../../departments/api/departmentsApi'
import type { Organization } from '@/modules/system/organizations/api/organizationsApi'
import type { Department } from '../../departments/api/departmentsApi'

type TreeNode = {
  id: string
  type: 'org' | 'dept'
  label: string
  code?: string | null
  isActive: boolean
  children: TreeNode[]
  raw: Organization | Department
}

function buildTree(orgs: Organization[], depts: Department[]): TreeNode[] {
  const orgMap = new Map<number, TreeNode>()
  const deptMap = new Map<number, TreeNode>()

  orgs.forEach((o) => {
    orgMap.set(o.organizationId, {
      id: `org-${o.organizationId}`,
      type: 'org',
      label: o.name,
      code: o.code,
      isActive: o.isActive,
      children: [],
      raw: o,
    })
  })

  depts.forEach((d) => {
    deptMap.set(d.departmentId, {
      id: `dept-${d.departmentId}`,
      type: 'dept',
      label: d.name,
      code: d.code,
      isActive: d.isActive,
      children: [],
      raw: d,
    })
  })

  depts.forEach((d) => {
    const deptNode = deptMap.get(d.departmentId)
    if (!deptNode) return
    if (d.parentId) {
      const parentDept = deptMap.get(d.parentId)
      if (parentDept) parentDept.children.push(deptNode)
    } else if (d.organizationId != null) {
      const orgNode = orgMap.get(d.organizationId)
      if (orgNode) orgNode.children.push(deptNode)
    }
  })

  return Array.from(orgMap.values())
}

function TreeNodeView({ node, level = 0, t }: { node: TreeNode; level?: number; t: (key: string) => string }) {
  const [expanded, setExpanded] = useState(level < 2)
  const hasChildren = node.children.length > 0
  const paddingLeft = level * 20 + 8

  return (
    <div className="select-none">
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
        style={{ paddingLeft }}
        onClick={() => setExpanded((e) => !e)}
      >
        {hasChildren ? (
          <span className="w-4 text-gray-500">{expanded ? '▼' : '▶'}</span>
        ) : (
          <span className="w-4" />
        )}
        <span className="font-medium">{node.code ? `${node.code} - ` : ''}{node.label}</span>
        {node.type === 'org' && <Badge variant="secondary" className="text-xs">{t('orgTree.workplace')}</Badge>}
        {node.type === 'dept' && <Badge variant="secondary" className="text-xs">{t('orgTree.department')}</Badge>}
        {!node.isActive && <Badge variant="warning">{t('orgTree.inactive')}</Badge>}
      </div>
      {hasChildren && expanded && (
        <div>
          {node.children.map((c) => (
            <TreeNodeView key={c.id} node={c} level={level + 1} t={t} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function OrgTreePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [tree, setTree] = useState<TreeNode[]>([])

  const load = useCallback(async () => {
    if (!tenantId) {
      setLoading(false)
      setTree([])
      return
    }
    setLoading(true)
    try {
      const [orgRes, deptRes] = await Promise.all([
        organizationsApi.list({ tenantId, pageSize: 500, isActive: null, includeDeleted: false }),
        departmentsApi.list({ tenantId, pageSize: 500, isActive: null, includeDeleted: false }),
      ])
      const nodes = buildTree(orgRes.items, deptRes.items)
      setTree(nodes)
    } catch (e: any) {
      toast({ title: t('orgTree.error'), description: e?.message ?? t('orgTree.loadFailed'), variant: 'danger' })
      setTree([])
    } finally {
      setLoading(false)
    }
  }, [tenantId, toast, t])

  useEffect(() => {
    load()
  }, [load])

  if (!tenantId) {
    return (
      <div className="p-6">
        <PageHeader title={t('orgTree.title')} subtitle={t('orgTree.selectTenant')} />
        <Card className="p-6 mt-4">{t('orgTree.noTenant')}</Card>
      </div>
    )
  }

  return (
    <div className="p-6">
      <PageHeader
        title={t('orgTree.title')}
        subtitle={t('orgTree.subtitle')}
        actions={<button onClick={load} className="btn btn-secondary">{t('orgTree.refresh')}</button>}
      />
      <Card className="mt-4 p-4">
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : tree.length === 0 ? (
          <p className="text-muted-foreground">{t('orgTree.empty')}</p>
        ) : (
          <div className="space-y-0.5">
            {tree.map((node) => (
              <TreeNodeView key={node.id} node={node} t={t} />
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
